// A tab strip that never scrolls sideways: critical tabs sit in the strip in
// priority order while they fit, everything else is one tap away behind a
// More menu. Used by the IP Case Sheet and Patient Records; the fitting rule
// is lib/priorityTabs.ts. Design reference: the "Tab navigation" section of
// docs/design/ip-case-sheet/IP_Case_Sheet_Wireframe.html.
//
// Which tabs fit is measured from the strip's own width (ResizeObserver), not
// from viewport breakpoints — both screens sit beside the app sidebar or
// inside a dialog, so the viewport says little about the space they actually
// have. Every tab is rendered once, invisibly, in a clipped measuring row so
// its natural width is known before deciding; the decision happens in a
// layout effect, so nothing paints at the wrong width first.
//
// The active tab is never promoted out of More into the strip: that would
// shift the other tabs sideways under the user's finger. The More button
// carries the active state instead — a small "MORE" over the section's name —
// and is measured at the width it actually shows. Choosing a long-named section
// from the menu can therefore hand one strip tab to the menu; that happens only
// as the menu closes, never while the user is pressing a strip tab, and it
// keeps the strip from spending its width on a label nobody is reading.
import { useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/cn';
import { fitStripTabs, menuGroups, type PriorityTab } from '@/lib/priorityTabs';

const GAP_PX = 4; // gap-1
const PLAIN_MORE = '__plain';

const tabClass = (on: boolean) => cn(
  'whitespace-nowrap border-b-2 px-3 py-2 text-[12.5px] font-medium transition-colors',
  'pointer-coarse:min-h-11 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-teal',
  on ? 'border-teal text-teal' : 'border-transparent text-ink-muted hover:text-foreground',
);

const moreClass = (on: boolean) => cn(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-t-md border border-b-2 px-3 text-[12.5px] font-medium transition-colors',
  'pointer-coarse:min-h-11 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal',
  on
    ? 'border-teal border-b-teal bg-background py-1 text-teal'
    : 'border-border border-b-transparent bg-muted/40 py-2 text-foreground hover:bg-muted',
);

function MoreLabel({ active }: { active?: string }) {
  return (
    <>
      {active ? (
        <span className="flex min-w-0 flex-col items-start leading-tight">
          <span className="text-[9.5px] font-semibold uppercase tracking-wide opacity-75">More</span>
          <span className="max-w-[8rem] truncate font-semibold">{active}</span>
        </span>
      ) : (
        <span>More</span>
      )}
      <ChevronDown aria-hidden className="size-3.5 shrink-0" />
    </>
  );
}

export interface PriorityTabNavProps<Id extends string> {
  tabs: readonly PriorityTab<Id>[];
  active: Id;
  onSelect: (id: Id) => void;
  /** Names the nav landmark, e.g. "Case sheet sections". */
  ariaLabel: string;
  /** Heading over critical tabs that overflowed into the menu. */
  coreLabel?: string;
  /** Heading over the always-in-More tabs. */
  secondaryLabel?: string;
}

export function PriorityTabNav<Id extends string>({
  tabs, active, onSelect, ariaLabel, coreLabel = 'Core', secondaryLabel = 'Other sections',
}: PriorityTabNavProps<Id>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fitted, setFitted] = useState<Set<Id> | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const compute = () => {
      const widths = new Map<Id, number>();
      measure.querySelectorAll<HTMLElement>('[data-tab]').forEach(el => {
        widths.set(el.dataset.tab as Id, el.getBoundingClientRect().width);
      });
      const moreWidth = (key: string) =>
        measure.querySelector<HTMLElement>(`[data-more="${key}"]`)?.getBoundingClientRect().width ?? 0;
      const available = container.clientWidth;
      let next = fitStripTabs(tabs, widths, available, moreWidth(PLAIN_MORE), GAP_PX);
      // The active tab is in the menu, so More shows its name: refit at that
      // width (it can only push more tabs into the menu, so the active one
      // stays there and the answer is stable).
      if (!next.has(active)) next = fitStripTabs(tabs, widths, available, moreWidth(active), GAP_PX);
      setFitted(prev => (
        prev && prev.size === next.size && [...next].every(id => prev.has(id)) ? prev : next
      ));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    // Widths change when web fonts finish loading, without the container moving.
    ro.observe(measure);
    return () => ro.disconnect();
  }, [tabs, active]);

  const { strip, core: coreInMenu, secondary, activeInMenu } = menuGroups(tabs, fitted, active);
  const hasMenu = coreInMenu.length + secondary.length > 0;

  const item = (t: PriorityTab<Id>) => {
    const on = t.id === active;
    return (
      <DropdownMenuItem
        key={t.id}
        onSelect={() => onSelect(t.id)}
        aria-current={on ? 'page' : undefined}
        className={cn('justify-between text-[13px] pointer-coarse:py-3', on && 'font-semibold text-teal')}
      >
        {t.label}
        {on && <Check aria-hidden className="text-teal" />}
      </DropdownMenuItem>
    );
  };

  return (
    <div ref={containerRef} className="relative min-w-0">
      {/* Measuring row: same classes as the real controls, clipped to zero
          height so it can be wider than the container without causing any
          horizontal overflow. */}
      <div aria-hidden className="pointer-events-none invisible absolute inset-x-0 top-0 h-0 overflow-hidden">
        <div ref={measureRef} className="flex w-max gap-1">
          {tabs.filter(t => t.group === 'critical').map(t => (
            <span key={t.id} data-tab={t.id} className={tabClass(true)}>{t.label}</span>
          ))}
          <span data-more={PLAIN_MORE} className={moreClass(false)}><MoreLabel /></span>
          {tabs.map(t => (
            <span key={t.id} data-more={t.id} className={moreClass(true)}><MoreLabel active={t.label} /></span>
          ))}
        </div>
      </div>

      <nav aria-label={ariaLabel} className="flex items-end gap-1 border-b border-border">
        {strip.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect(t.id)}
            aria-current={t.id === active ? 'page' : undefined}
            className={tabClass(t.id === active)}
          >
            {t.label}
          </button>
        ))}
        {hasMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={activeInMenu ? `More sections, current: ${activeInMenu.label}` : 'More sections'}
                title={activeInMenu ? `More — ${activeInMenu.label}` : undefined}
                className={cn(moreClass(!!activeInMenu), 'ml-auto')}
              >
                <MoreLabel active={activeInMenu?.label} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              collisionPadding={8}
              className="w-[min(16rem,calc(100vw-16px))]"
            >
              {coreInMenu.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-ink-muted">{coreLabel}</DropdownMenuLabel>
                  {coreInMenu.map(item)}
                </>
              )}
              {coreInMenu.length > 0 && secondary.length > 0 && <DropdownMenuSeparator />}
              {secondary.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-ink-muted">{secondaryLabel}</DropdownMenuLabel>
                  {secondary.map(item)}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </nav>
    </div>
  );
}
